import { of, switchMap, tap, merge, fromEvent, distinctUntilChanged, map, mergeMap, Observable, finalize, BehaviorSubject, delay } from 'rxjs';

import { replaceViewContainer, renderTemplate, getParentAttr } from './ui.js';
import { collections$, importFile } from './knowledge-api.js';
import { buildWorkMonitor } from './working.js';

let fileMetadataFields = [
    ['source URI', 'source_uri'],
];

export function createImportView() {
    return of(undefined).pipe(
        switchMap(() => {
            let root = renderTemplate('import-view');
            replaceViewContainer(root);

            let saveMonitor = buildWorkMonitor();
            let saveIndicator$ = saveMonitor.showElements('.saving');
            let alertSavingBeforeUnload$ = saveMonitor.alertBeforeUnload();

            let form = root.querySelector('form[name=importFilesForm]');

            let remainingFiles$ = new BehaviorSubject(0);
            let renderRemainingFiles$ = remainingFiles$.pipe(
                distinctUntilChanged(),
                tap(remainingFiles => root.querySelector('.remaining-files').textContent = `${remainingFiles}`),
            );

            let renderFileMetadataInputs$ = merge(fromEvent(form, 'reset'), fromEvent(form.uploadedFiles, 'change')).pipe(
                // delay is necessary so that we trigger AFTER the
                // form reset
                delay(0),
                tap(() => {
                    let container = root.querySelector('.files-metadata');
                    container.innerHTML = '';
                    let files = form.uploadedFiles.files;
                    for (let i = 0; i < files.length; ++i) {
                        let file = files[i];
                        let fileEl = document.createElement('div');
                        fileEl.setAttribute('data-file-index', `f-${i}`);
                        fileEl.classList.add('file-container');
                        let fileNameEl = document.createElement('div');
                        fileNameEl.classList.add('file-name');
                        fileNameEl.textContent = file.name;
                        fileEl.appendChild(fileNameEl);
                        let fileMetaEl = document.createElement('div');
                        fileMetaEl.classList.add('file-meta-container');
                        for (let [title, docAttr] of fileMetadataFields) {
                            let metaEl = renderMetadataInput(title);
                            metaEl.setAttribute('data-document-attribute', docAttr);
                            metaEl.classList.add('file-meta-value');
                            fileMetaEl.appendChild(metaEl);
                        }
                        fileEl.appendChild(fileMetaEl);
                        container.appendChild(fileEl);
                    }
                }),
            );

            let renderCollectionOptions$ = collections$.pipe(
                tap(collections => {
                    let select = form.targetCollection;
                    select.innerHTML = '';
                    if (collections.length !== 1) {
                        let opt = document.createElement('option');
                        opt.textContent = '<select>';
                        opt.value = '';
                        select.appendChild(opt);
                    }
                    for (let col of collections) {
                        let opt = document.createElement('option');
                        opt.value = `${col.id}`;
                        opt.textContent = `${col.owner}/${col.name}`;
                        select.appendChild(opt);
                    }
                }),
            );

            let submit$ = fromEvent(form, 'submit').pipe(
                map(event => {
                    event.preventDefault();
                    let formValues = {
                        colId: parseInt(form.targetCollection.value),
                        files: [...form.uploadedFiles.files].map((file, i) => ({
                            file,
                            meta: [
                                { key: 'file_name', value: file.name },
                                ...fileMetadataFields.map(([title, docAttr]) => {
                                    return {
                                        key: docAttr,
                                        value: (form.querySelector(`form[name=importFilesForm] .file-container[data-file-index=f-${i}] .file-meta-value[data-document-attribute=${docAttr}] input`).value || '').trim() || null,
                                    };
                                }),
                            ],
                        })),
                    };
                    remainingFiles$.next(remainingFiles$.value + formValues.files.length);
                    form.reset();
                    return formValues;
                }),
                mergeMap(formValues => new Observable(observer => {
                    let colId = formValues.colId;
                    for (let file of formValues.files) {
                        observer.next({
                            colId,
                            file,
                        });
                    }
                    observer.complete();
                }), undefined, 1),
                mergeMap(({ colId, file }) => of(undefined).pipe(
                    switchMap(() => saveMonitor.zone(importFile(colId, file.file, file.meta))),
                    finalize(() => remainingFiles$.next(remainingFiles$.value - 1)),
                ), undefined, 1),
            );

            return merge(
                saveIndicator$,
                alertSavingBeforeUnload$,
                renderRemainingFiles$,
                renderFileMetadataInputs$,
                renderCollectionOptions$,
                submit$,
            );
        }),
    );
}

function renderMetadataInput(title) {
    let fc = document.createElement('div');
    fc.classList.add('form-control');
    let label = document.createElement('label');
    label.textContent = title;
    fc.appendChild(label);
    let input = document.createElement('input');
    input.type = 'text';
    input.placeholder = title;
    fc.appendChild(input);
    return fc;
}
